import { useDepot } from '#~/depot/hooks.js';
import { compareIds } from '#~/models/index.js';
import { useRoom } from '#~/room/hooks.js';
import { Sel, STag } from '#~/stag/tag.js';
import { Placeholder } from '#~/video/components/card/index.js';
import { Arrangement } from '#~/video/index.js';
import { Ikon } from '@studio/design-system/button';
import { Column } from '@studio/design-system/form';
import { Motions } from '@studio/design-system/motion';
import { Reorder, useDragging, useOrderedIds } from '@studio/design-system/motion/reorder';
import type { Device } from '@studio/models/device';
import { useLatestFn, useLazyRef } from '@studio/react-utils/hooks';
import { isNotEmpty } from '@tryiris-ai/js-types';
import { dequal } from 'dequal/lite';
import type { ReactElement } from 'react';
import { memo } from 'react';
import { twMerge } from 'cn';
import { Item } from './item/index.js';
import type { ListProps } from './types.js';

/** @internal */
const Inner = ({
  arrangement,
  className,
  devices,
  handler,
  selectedId,
  canControl: _canControl,
  ...props
}: ListProps): ReactElement => {
  // Drag/Drop
  const [orderedIds, setOrderedIds] = useOrderedIds(devices.map((µ) => µ.id));
  const [isDragging, setDragging] = useDragging();

  const handleDragStart = useLatestFn(() => {
    setDragging(true);
  });

  const handleDragEnd = useLatestFn(() => {
    setDragging(false);

    // Has the order actually changed?
    if (!compareIds(devices, orderedIds)) {
      handler.onReorder(orderedIds);
    }
  });

  const handleSelect = useLatestFn((deviceId: Device['id']) => {
    // Ignore click events while dragging.
    if (!isDragging) {
      handler.onSelect(deviceId);
    }
  });

  // Override `onSelect` handler.
  const passthru = useLazyRef(() => ({
    ...handler,
    onSelect: handleSelect
  })).current;

  const roomId = useRoom((µ) => µ.id);
  const canControl = useDepot((u) => u.roomRecords[roomId]?.permissions.canControl);

  if (!canControl) {
    return (
      <Column className={twMerge('p-16', className)} {...props}>
        {isNotEmpty(devices) &&
          devices.map((device) => (
            <Item
              key={device.id}
              device={device}
              handler={passthru}
              canControl={!!canControl && !device.pending}
              isSelected={device.id === selectedId}
            />
          ))}
      </Column>
    );
  }

  return (
    <Column className={twMerge('p-16', className)} {...props}>
      {isNotEmpty(orderedIds) ? (
        <Reorder.Group
          axis='y'
          className={twMerge(
            'flex flex-col',
            arrangement === Arrangement.Single ? 'gap-16' : 'gap-12'
          )}
          layoutScroll
          onReorder={setOrderedIds}
          values={orderedIds}
        >
          {orderedIds.map((µ) => {
            const device = devices.find((p) => p.id === µ);

            // Sanity check
            if (!device) {
              return null;
            }

            return (
              <Reorder.Item
                {...Motions.Fade}
                key={device.id}
                onDragEnd={handleDragEnd}
                onDragStart={handleDragStart}
                value={device.id}
                {...Motions.Drag}
                {...Motions.Fade}
              >
                {/* Be very careful w/ memoization here! */}
                <Item
                  device={device}
                  handler={passthru}
                  canControl={!!canControl && !device.pending}
                  isCollapsed={arrangement !== Arrangement.Single}
                  isSelected={device.id === selectedId}
                  {...STag(Sel.MainRoomLeftTrayVideo, 'devices', device.id)}
                />
              </Reorder.Item>
            );
          })}
        </Reorder.Group>
      ) : (
        <Placeholder onClick={handler.onAdd} />
      )}

      {!!_canControl && (
        <Ikon
          className='w-32 self-center text-lg'
          icon='plus'
          onClick={handler.onAdd}
          {...STag(Sel.MainRoomLeftTrayAudio, 'footer.add')}
        />
      )}
    </Column>
  );
};

/** Video `<List />` component. */
export const List = memo(Inner, dequal);
